import { SelectProvider } from '@ariakit/react'
import type { ReactElement } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { Chip } from '@/components/commons/chip'
import {
	SelectItem,
	SelectList,
	SelectPopover,
	SelectTrigger
} from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/dates'
import { useGetServiceVersions } from '@/features/service-vetting/query/use-get-service-versions.ts'
import type { ServiceStatus } from '@/features/service-vetting/types.ts'
import { serviceVettingStatusToLabel } from '@/features/service-vetting/utils.ts'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

export const ServiceVettingCriteriaVersionSelect = ({
	serviceId
}: {
	serviceId: number
}) => {
	const { t } = useTranslation()
	const versionQuery = useGetServiceVersions({
		serviceId
	})
	const navigate = useNavigate()

	const versionStatusToBadge = (status: ServiceStatus): ReactElement => {
		const variant = getStatusVariant(status)
		return (
			<Chip
				isActive
				label={t(serviceVettingStatusToLabel[status])}
				shape={'circle'}
				variant={variant}
			/>
		)
	}

	const getStatusVariant = (
		status: ServiceStatus
	): 'success' | 'danger' | 'warning' | 'info' | 'neutral' => {
		switch (status) {
			case 'approved':
			case 'publishing_enabled':
			case 'pending_publishing':
			case 'pending_export':
			case 'published_and_upgraded':
				return 'success'
			case 'declined':
				return 'danger'
			case 'update_required':
			case 'approval_required':
			case 'pending_upgrade_api':
			case 'pending_licensing':
				return 'warning'
			case 'under_review':
				return 'info'
			default:
				return 'neutral'
		}
	}

	return (
		<SelectProvider
			defaultValue={serviceId.toString()}
			setValue={(value) =>
				navigate(
					generatePath(routes.SERVICE_VETTING_CRITERIA.path, {
						id: value
					})
				)
			}
		>
			{versionQuery.isLoading ? (
				<div className='flex items-center gap-2'>
					<div className='flex gap-2'>
						<Skeleton height={20} width={100} />
						<Skeleton height={20} width={100} />
					</div>
					<Skeleton height={14} width={14} />
				</div>
			) : (
				<SelectTrigger
					className='border-none p-0 no-underline [&:disabled]:bg-neutral-100 [&:focus]:bg-transparent [&:focus]:shadow-none [&:focus]:outline-none [&:not(:disabled):hover]:bg-transparent [&:not(:disabled):hover]:shadow-none [&:not(:disabled):hover]:outline-none [&[aria-expanded="true"]]:bg-transparent [&[aria-expanded="true"]]:shadow-none [&[aria-expanded="true"]]:outline-none'
					displayValue={(value) => {
						const version = versionQuery.data?.find(
							(version) => version.id.toString() === value
						)
						const index = versionQuery.data?.findIndex(
							(version) => version.id.toString() === value
						)
						return (
							<div className='flex'>
								{version && index !== undefined && (
									<div className='flex items-center gap-2'>
										<h4 className='underline'>
											{t(
												'vetting:criteria.service.submission_number',
												{ value: index + 1 }
											)}
										</h4>
										{versionStatusToBadge(version.status)}
									</div>
								)}
							</div>
						)
					}}
					variant={'tertiary'}
				/>
			)}
			<SelectPopover sameWidth={false} unmountOnHide>
				<SelectList>
					{versionQuery.remappedData.map((value, index) => (
						<SelectItem key={value.id} value={value.id.toString()}>
							<div className='flex w-full justify-between gap-2'>
								<div className='flex items-center gap-2 text-sm'>
									<h4>
										{t(
											'vetting:criteria.service.submission_number',
											{ value: index + 1 }
										)}
									</h4>

									<p>{formatDate(value.created_at, 'PP')}</p>
								</div>
								{versionStatusToBadge(value.status)}
							</div>
						</SelectItem>
					))}
				</SelectList>
			</SelectPopover>
		</SelectProvider>
	)
}
